import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { SNACKBAR_DURATION_ERROR } from '../../app.constants';
import { AdminGroup, AdminGroupsService } from '../admin-groups.service';
import { AdminUserGroup, AdminUsersService } from '../admin-users.service';

export type AdminUserGroupsDialogData = {
	userId: string;
	email: string;
};

@Component({
	selector: 'app-admin-user-groups-dialog',
	imports: [MatDialogModule, MatButtonModule, MatIconModule, MatListModule, MatProgressSpinnerModule, MatFormFieldModule, MatSelectModule],
	templateUrl: './admin-user-groups-dialog.component.html',
	styleUrl: './admin-user-groups-dialog.component.scss',
})
export class AdminUserGroupsDialogComponent implements OnInit {
	private readonly usersService = inject(AdminUsersService);
	private readonly groupsService = inject(AdminGroupsService);
	private readonly snackBar = inject(MatSnackBar);
	readonly dialogRef = inject(MatDialogRef<AdminUserGroupsDialogComponent>);

	readonly data = inject<AdminUserGroupsDialogData>(MAT_DIALOG_DATA);

	userGroups = signal<AdminUserGroup[]>([]);
	allGroups = signal<AdminGroup[]>([]);
	loading = signal(true);
	selectedGroupName = signal<string | null>(null);
	adding = signal(false);

	availableGroups = computed(() => {
		const assigned = new Set(this.userGroups().map(g => g.groupName));
		return this.allGroups().filter(g => !assigned.has(g.groupName));
	});

	async ngOnInit() {
		try {
			const [userGroups, allGroups] = await Promise.all([
				firstValueFrom(this.usersService.getUserGroups(this.data.userId)),
				firstValueFrom(this.groupsService.getGroups()),
			]);
			this.userGroups.set(userGroups);
			this.allGroups.set(allGroups);
		} catch {
			this.snackBar.open(
				$localize`:@@FailedToLoadGroups:Failed to load groups`,
				undefined,
				{ duration: SNACKBAR_DURATION_ERROR }
			);
		} finally {
			this.loading.set(false);
		}
	}

	async addGroup() {
		const groupName = this.selectedGroupName();
		if (!groupName) return;
		this.adding.set(true);
		try {
			const result = await firstValueFrom(this.usersService.addUserToGroup(this.data.userId, groupName));
			if (result.success) {
				this.userGroups.update(groups => [...groups, { groupName }]);
				this.selectedGroupName.set(null);
			} else {
				this.snackBar.open(
					$localize`:@@FailedWithMessage:Failed: ${result.errorMessage}`,
					$localize`:@@Close:Close`,
					{ duration: SNACKBAR_DURATION_ERROR }
				);
			}
		} finally {
			this.adding.set(false);
		}
	}

	async removeGroup(groupName: string) {
		const result = await firstValueFrom(this.usersService.removeUserFromGroup(this.data.userId, groupName));
		if (result.success) {
			this.userGroups.update(groups => groups.filter(g => g.groupName !== groupName));
		} else {
			this.snackBar.open(
				$localize`:@@FailedWithMessage:Failed: ${result.errorMessage}`,
				$localize`:@@Close:Close`,
				{ duration: SNACKBAR_DURATION_ERROR }
			);
		}
	}
}
